import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import { TextInput, TextareaInput } from "@/components/InputFields"
import EstimatedMacrosInfoModal from "@/screens/measurements/components/EstimatedMacrosInfoModal"
import MealInfoModal from "@/screens/measurements/components/MealInfoModal"
import { zodResolver } from "@hookform/resolvers/zod"
import { InfoCircledIcon } from "@radix-ui/react-icons"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

const FOOD_INFO_PLACEHOLDER =
  "[e.g. 1 cup grilled chicken, 1/2 cup rice, 1/2 cup broccoli, 6 oz unsweetened iced tea, 1/4 cup raspberries]"
const ESTIMATED_MACROS = "Estimated macros (g)"
const CARB_LABEL = "Carbs"
const PROTEIN_LABEL = "Protein"
const NEXT_BUTTON = "Continue"

const formSchema = z.object({
  mealContents: z.string(),
  carbsInGrams: z
    .string()
    .refine((value) => value === "" || /^\d+$/.test(value), "Carbs must be a whole number"),
  proteinInGrams: z
    .string()
    .refine((value) => value === "" || /^\d+$/.test(value), "Protein must be a whole number"),
})

type MealDetailsFormValues = z.infer<typeof formSchema>

type Props = {
  mealContentTitle: string
  context: string
  initialValues: {
    mealContents: string
    carbsInGrams: string | number
    proteinInGrams: string | number
  }
  onSubmit: (values: {
    mealContents: string
    carbsInGrams: string | number
    proteinInGrams: string | number
  }) => void
  onBack?: () => void
}

const MealDetailsStep = ({ mealContentTitle, context, initialValues, onSubmit, onBack }: Props) => {
  const [mealInfoOpen, setMealInfoOpen] = useState(false)
  const [macrosInfoOpen, setMacrosInfoOpen] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MealDetailsFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(formSchema),
    defaultValues: {
      mealContents: initialValues.mealContents,
      carbsInGrams: initialValues.carbsInGrams === "" ? "" : String(initialValues.carbsInGrams),
      proteinInGrams:
        initialValues.proteinInGrams === "" ? "" : String(initialValues.proteinInGrams),
    },
  })

  const submit = (values: MealDetailsFormValues) =>
    onSubmit({
      mealContents: values.mealContents,
      carbsInGrams: values.carbsInGrams === "" ? "" : Number(values.carbsInGrams),
      proteinInGrams: values.proteinInGrams === "" ? "" : Number(values.proteinInGrams),
    })

  return (
    <form className="flex min-h-[calc(100vh-32px)] flex-col gap-4" onSubmit={handleSubmit(submit)}>
      {onBack && <BackButton customClickHandler={onBack} />}
      <h2 className="text-h2 text-primary-contrast">{context}</h2>

      <div className="mt-6 flex flex-1 flex-col gap-2">
        <div className="flex items-center gap-1">
          <Label htmlFor="mealContents" className="text-h4">
            {mealContentTitle}
          </Label>
          <button
            type="button"
            onClick={() => setMealInfoOpen(true)}
            aria-label="About logging meals"
            className="text-primary-contrast/70 hover:text-primary-contrast"
          >
            <InfoCircledIcon className="h-4 w-4" />
          </button>
        </div>
        <TextareaInput
          id="mealContents"
          placeholder={FOOD_INFO_PLACEHOLDER}
          isError={!!errors.mealContents}
          className="min-h-[10rem] flex-1 resize-none"
          {...register("mealContents")}
        />
        {errors.mealContents && <p className="text-sm text-error">{errors.mealContents.message}</p>}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Label className="text-h4">{ESTIMATED_MACROS}</Label>
          <button
            type="button"
            onClick={() => setMacrosInfoOpen(true)}
            aria-label="About estimated macros"
            className="text-primary-contrast/70 hover:text-primary-contrast"
          >
            <InfoCircledIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="flex gap-4">
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="carbsInGrams">{CARB_LABEL}</Label>
            <div className="relative">
              <TextInput
                id="carbsInGrams"
                type="number"
                min={0}
                max={1000}
                step={1}
                placeholder="20"
                isError={!!errors.carbsInGrams}
                className="pr-8"
                {...register("carbsInGrams")}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-primary-contrast/60">
                g
              </span>
            </div>
            {errors.carbsInGrams && (
              <p className="text-sm text-error">{errors.carbsInGrams.message}</p>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="proteinInGrams">{PROTEIN_LABEL}</Label>
            <div className="relative">
              <TextInput
                id="proteinInGrams"
                type="number"
                min={0}
                max={1000}
                step={1}
                placeholder="15"
                isError={!!errors.proteinInGrams}
                className="pr-8"
                {...register("proteinInGrams")}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-primary-contrast/60">
                g
              </span>
            </div>
            {errors.proteinInGrams && (
              <p className="text-sm text-error">{errors.proteinInGrams.message}</p>
            )}
          </div>
        </div>
      </div>

      <Button type="submit" className="w-full py-2 text-base">
        {NEXT_BUTTON}
      </Button>

      <MealInfoModal open={mealInfoOpen} onClose={() => setMealInfoOpen(false)} />
      <EstimatedMacrosInfoModal open={macrosInfoOpen} onClose={() => setMacrosInfoOpen(false)} />
    </form>
  )
}

export default MealDetailsStep
